import classNames from 'clsx';
import { get as lodashGet } from 'lodash';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Html } from '@swf/cms';
import useTrackers from '@swf/core/hooks/useTrackers';
import { RootHistoryContext } from '@swf/core/providers/RootHistoryContextProvider';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { absoluteURL, canUseDOM } from '@swf/core/utils';
import { routeHandler } from '@swf/core/utils/c2l';

import styles from './CMSHTML.scss';

interface Props extends Html {
  className?: string;
  tag?: string;
}

const hrefMatch = /href=(["'])(\/[^/][^"']*)["']/g;
const noEmbeddedHrefMatch = /href=(["'])(\/(?!embedded)[^/][^"']*)["']/g;
const externalLinkMatch = /href=["'](\/[^][^"']*)["']/gm;

const CMSHTML: React.FC<Props> = ({ content, tag = 'div', className }) => {
  const { modal } = useDispatch<Dispatch>();
  const history = useContext(RootHistoryContext);
  const origin = useSelector(select.location.getOrigin);
  const localePrefix = useSelector(select.location.getLocaleURL);
  const baseUrl = useSelector(select.location.getBaseURL) || '';
  const isEmbeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const isEmbedded = useSelector(select.site.isEmbedded);
  const { buttonPressed, linkClicked } = useTrackers();

  if (!content) return null;

  if (isEmbedded) {
    const replacer = (_: string, path: string) => `href="external-${origin}${path}"`;

    content = content
      .split('<')
      .map((link) =>
        link.includes('class="external"') ? link.replace(externalLinkMatch, replacer) : link
      )
      .join('<');
  }

  if (isEmbeddedRoute) {
    content = content.replace(noEmbeddedHrefMatch, `href=$1/embedded$2$1`);
  }

  if (localePrefix) {
    content = content.replace(hrefMatch, `href=$1${localePrefix}$2$1`);
  }

  const trackClick: React.MouseEventHandler = (event) => {
    const button = (event.target as HTMLElement).closest('button');
    let trackId = button?.getAttribute('data-track-id');
    if (trackId) {
      buttonPressed({ buttonName: trackId });
    }

    const a = (event.target as HTMLElement).closest('a');
    trackId = a?.getAttribute('data-track-id');
    if (trackId) {
      const href = a?.getAttribute('href');
      linkClicked({
        destinationUrl: href || '',
        linkText: trackId
      });
    }
  };

  const onClick: React.MouseEventHandler = (event) => {
    trackClick(event);
    const anchorElement = (event.target as HTMLElement).closest('a');
    if (!anchorElement) return;

    const href = anchorElement.getAttribute('href');
    if (history && routeHandler(href, history, event)) {
      return;
    }

    if (isEmbedded && anchorElement.hasAttribute('c2lEmbedded') && href) {
      event.preventDefault();
      canUseDOM && lodashGet(window, ['starsweb', 'app', 'execute'], () => undefined)(href);
      return;
    }

    if (
      href &&
      !absoluteURL(href) &&
      !href.startsWith('#') &&
      !anchorElement.hasAttribute('download') &&
      anchorElement.getAttribute('target') !== '_top'
    ) {
      if (anchorElement.hasAttribute('data-hard-link')) {
        window.location.assign(href);
        return;
      }

      if (href.indexOf('#') === -1) {
        event.preventDefault();
      }
      history?.push(href.replace(baseUrl, ''));
      modal.close();
    }
  };

  return React.createElement(tag, {
    dangerouslySetInnerHTML: { __html: content },
    className: classNames(styles.htmlContent, className),
    onClick
  });
};
export default CMSHTML;
